/**
 * Every stable error code the API can return.
 *
 * A client branches on these and translates them itself. `message` from the
 * envelope is already-translated prose for a person to read; this is the part
 * code is allowed to look at.
 *
 * Keep this list in step with the `DomainError` codes thrown in
 * `apps/backend/src` — a code with no translation renders as a raw
 * SHOUTING_SNAKE string, which is how a missing one announces itself.
 */
/** Transport-level, derived from the HTTP status by the exception filter. */
export const TRANSPORT_ERROR_CODES = [
    'BAD_REQUEST',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'PAYLOAD_TOO_LARGE',
    'UNSUPPORTED_MEDIA_TYPE',
    'RATE_LIMITED',
    'INTERNAL_ERROR',
    'SERVICE_UNAVAILABLE',
    'VALIDATION_ERROR',
];
/** Raised by domain services, each carrying its own meaning. */
export const DOMAIN_ERROR_CODES = [
    // auth + users
    'ACCOUNT_DISABLED',
    'CANNOT_DEACTIVATE_SELF',
    'CANNOT_DEMOTE_SELF',
    'EMAIL_TAKEN',
    'INVALID_CREDENTIALS',
    'INVALID_REFRESH_TOKEN',
    'LAST_OWNER',
    'NO_REFRESH_TOKEN',
    'PASSWORD_CHANGE_REQUIRED',
    'PASSWORD_UNCHANGED',
    'REFRESH_TOKEN_EXPIRED',
    'REFRESH_TOKEN_REUSED',
    // concurrency
    'STALE_CONTENT_REVISION',
    'VERSION_CONFLICT',
    'CONTENT_REVISION_INVALID',
    // publication + lifecycle
    'CATEGORY_ARCHIVED',
    'CATEGORY_NOT_FOUND',
    'GROUP_ARCHIVED',
    'GROUP_NOT_FOUND',
    'HAS_DEPENDENTS',
    'NOT_ARCHIVED',
    'NOT_PUBLISHABLE',
    'WOULD_UNPUBLISH',
    'TYPE_CHANGE_CLEARS_ITEMS',
    // lists
    'DUPLICATE_IDS',
    'DUPLICATE_ITEMS',
    'INCOMPLETE_LIST',
    'UNKNOWN_IDS',
    'UNKNOWN_ITEMS',
    'TOO_MANY_FEATURED',
    // offers + pricing
    'ANNOUNCEMENT_HAS_NO_PRICES',
    'MENU_NOT_CONFIGURED',
    'OFFER_OVERLAP',
    'PRICE_BREAKS_OFFERS',
    'INVALID_CURRENCY',
    'CURRENCY_CHANGE_NEEDS_CONFIRMATION',
    // branches, hours + closures
    'BRANCH_NOT_PUBLISHED',
    'CLOSURE_OVERLAP',
    'COORDINATE_OUT_OF_RANGE',
    'INVALID_COORDINATE',
    'INVALID_SCHEDULE',
    'INVALID_TIMEZONE',
    'INTERVALS_REQUIRED',
    // media
    'FILE_REQUIRED',
    'MEDIA_IN_USE',
    'STORAGE_UNAVAILABLE',
    'UNSUPPORTED_IMAGE_TYPE',
    // settings
    'DUPLICATE_SOCIAL_LINK',
    'UNSAFE_URL',
    'HTTPS_URL_REQUIRED',
    'ORIGIN_NOT_ALLOWED',
    'ORIGIN_REQUIRED',
];
/** Per-field codes, arriving inside `error.fieldErrors`. */
export const FIELD_ERROR_CODES = [
    'REQUIRED',
    'TOO_LONG',
    'INVALID_ID',
    'INVALID_DATE',
    'INVALID_DATE_RANGE',
    'INVALID_TIME',
    'END_BEFORE_START',
    'EMAIL_INVALID',
    'EMAIL_TOO_LONG',
    'PASSWORD_REQUIRED',
    'PASSWORD_TOO_SHORT',
    'NON_NEGATIVE_DECIMAL_REQUIRED',
    // Raised by the publish validator, inside `fieldErrors`, when a record is
    // refused. They say which field is not ready and why.
    'REQUIRED_FOR_PUBLICATION',
    'TRANSLATION_REQUIRED',
    'IMAGE_TOO_SMALL',
    'CATEGORY_NOT_PUBLISHED',
    'TIMEZONE_NOT_CONFIGURED',
    'AT_LEAST_ONE_ITEM_REQUIRED',
    'NOT_BELOW_REGULAR_PRICE',
    'REGULAR_PRICE_IS_ZERO',
    'PRICE_OUT_OF_RANGE',
    'OUT_OF_RANGE',
];
export const ALL_ERROR_CODES = [
    ...TRANSPORT_ERROR_CODES,
    ...DOMAIN_ERROR_CODES,
    ...FIELD_ERROR_CODES,
];
const KNOWN = new Set(ALL_ERROR_CODES);
export function isKnownErrorCode(code) {
    return KNOWN.has(code);
}
/**
 * Codes that mean "someone else changed this while you were editing".
 *
 * They are the one error class worth a distinct treatment in the UI: the edit
 * is not wrong, it is merely late, and the fix is to reload rather than to
 * correct a field.
 */
export const CONCURRENCY_CODES = [
    'VERSION_CONFLICT',
    'STALE_CONTENT_REVISION',
];
export function isConcurrencyError(code) {
    return CONCURRENCY_CODES.includes(code);
}
/**
 * Codes the server answers with the counts of what an action would affect, and
 * which the client re-sends with a confirmation flag once a person has agreed.
 */
export const CONFIRMABLE_CODES = [
    'HAS_DEPENDENTS',
    'CURRENCY_CHANGE_NEEDS_CONFIRMATION',
    'TYPE_CHANGE_CLEARS_ITEMS',
];
export function isConfirmableError(code) {
    return CONFIRMABLE_CODES.includes(code);
}
//# sourceMappingURL=errors.js.map